import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Currency } from '@/components/ui/currency'
import { ArrowLeft, Calendar, MapPin, Eye, Clock, User, Target, DollarSign, AlertCircle } from 'lucide-react'
import { useParams, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { projectApi, projectReportApi } from '@/lib/client'
import type { ProjectReport } from '@/types'

export default function ProjectDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  // Fetch single project by id
  const { data: projectData, isLoading, error } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectApi.getProjectById(id!),
    enabled: !!id
  })

  // Fetch project reports separately
  const { data: reportsData, isLoading: isReportsLoading } = useQuery({
    queryKey: ['project-reports', id],
    queryFn: () => projectReportApi.getReports({ projectId: id }),
    enabled: !!id
  })

  const project = projectData?.data
  const reports = reportsData?.data || []

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="h-64 bg-gray-200 rounded-xl mb-8"></div>
              <div className="space-y-4">
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Button
              onClick={() => navigate('/projects')}
              className="mb-6 bg-white/20 hover:bg-white/30 text-blue-600 border border-blue-200"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Kembali ke Proyek
            </Button>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Proyek tidak ditemukan</h3>
                <p className="text-gray-500 mb-6">Proyek yang Anda cari mungkin telah dihapus atau tidak tersedia.</p>
                <Button onClick={() => navigate('/projects')}>
                  Kembali ke Halaman Proyek
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'PLANNED': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Perencanaan' },
      'IN_PROGRESS': { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'Sedang Berjalan' },
      'COMPLETED': { color: 'bg-green-100 text-green-800 border-green-200', text: 'Selesai' },
      'CANCELLED': { color: 'bg-red-100 text-red-800 border-red-200', text: 'Dibatalkan' }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig['PLANNED']
    return <Badge className={config.color}>{config.text}</Badge>
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header with Back Button */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 backdrop-blur-sm bg-white/90">
        <div className="container mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/projects')}
            variant="ghost"
            className="hover:bg-gray-100 text-gray-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kembali ke Proyek
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Project Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {getStatusBadge(project.status)}
              {/* Display categories */}
              {project.categories && project.categories.length > 0 && (
                <>
                  {project.categories.map((category) => (
                    <Badge key={category.id} variant="outline" className="border-blue-300 text-blue-600 bg-blue-50">
                      {category.name}
                    </Badge>
                  ))}
                </>
              )}
              {/* Fallback to single category for backward compatibility */}
              {!project.categories && project.category && (
                <Badge variant="outline" className="border-gray-300 text-gray-600">
                  {project.category.name}
                </Badge>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {project.title}
            </h1>

            {/* Tags Section */}
            {project.tags && project.tags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <Badge 
                      key={tag.id} 
                      variant="secondary" 
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                    >
                      #{tag.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Featured Image */}
            {(project.featuredImage || project.image) && (
              <div className="mb-8">
                <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={project.featuredImage || project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Project Meta */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 pb-6 border-b border-gray-200">
              {project.startDate && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <div>
                    <p className="font-medium text-gray-700">Tanggal Mulai</p>
                    <p>{new Date(project.startDate).toLocaleDateString('id-ID')}</p>
                  </div>
                </div>
              )}
              {project.endDate && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4 text-green-500" />
                  <div>
                    <p className="font-medium text-gray-700">Tanggal Selesai</p>
                    <p>{new Date(project.endDate).toLocaleDateString('id-ID')}</p>
                  </div>
                </div>
              )}
              {project.location && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <MapPin className="h-4 w-4 text-red-500" />
                  <div>
                    <p className="font-medium text-gray-700">Lokasi</p>
                    <p>{project.location}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Coordinator and Budget Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {project.coordinatorName && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    {project.coordinatorAvatar ? (
                      <img 
                        src={project.coordinatorAvatar} 
                        alt={project.coordinatorName}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <User className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{project.coordinatorName}</p>
                    <p className="text-sm text-gray-600">{project.coordinatorPosition}</p>
                    <p className="text-xs text-blue-600 font-medium">Koordinator Proyek</p>
                  </div>
                </div>
              )}
              {project.budget && (
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Anggaran Total</p>
                    <p className="text-lg font-bold text-green-600">
                      <Currency amount={project.budget} format="standard" />
                    </p>
                    {project.budgetUsed && (
                      <p className="text-sm text-gray-600">
                        Terpakai: <Currency amount={project.budgetUsed} format="standard" />
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Project Details - Single Column Layout */}
          <div className="max-w-4xl mx-auto space-y-8 mb-8">
            {/* Description */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-500" />
                  Deskripsi Proyek
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: project.description || '' }}
                />
              </CardContent>
            </Card>

            {/* Progress Proyek */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-500" />
                  Progress Proyek
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Kemajuan</span>
                      <span className="text-sm text-gray-500">{project.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${project.progress || 0}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Project Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    {project.startDate && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Tanggal Mulai</p>
                          <p className="text-sm text-gray-600">
                            {new Date(project.startDate).toLocaleDateString('id-ID', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {project.endDate && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-red-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Target Selesai</p>
                          <p className="text-sm text-gray-600">
                            {new Date(project.endDate).toLocaleDateString('id-ID', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    )}

                    {project.budget && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Anggaran</p>
                          <p className="text-sm text-gray-600">
                            <Currency amount={project.budget} />
                          </p>
                        </div>
                      </div>
                    )}

                    {project.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-purple-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Lokasi</p>
                          <p className="text-sm text-gray-600">{project.location}</p>
                        </div>
                      </div>
                    )}

                    {project.coordinatorName && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-indigo-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Koordinator</p>
                          <p className="text-sm text-gray-600">{project.coordinatorName}</p>
                          {project.coordinatorPosition && (
                            <p className="text-xs text-gray-500">{project.coordinatorPosition}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tags and Categories */}
                  {((project.tags && project.tags.length > 0) || (project.categories && project.categories.length > 0)) && (
                    <div className="pt-4 border-t border-gray-200">
                      {project.tags && project.tags.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-2">Tags:</p>
                          <div className="flex flex-wrap gap-1">
                            {project.tags.map((tag) => (
                              <Badge key={tag.id} variant="secondary" className="text-xs">
                                {tag.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {project.categories && project.categories.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-2">Kategori:</p>
                          <div className="flex flex-wrap gap-1">
                            {project.categories.map((category) => (
                              <Badge key={category.id} variant="outline" className="text-xs">
                                {category.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

              {/* Reports */}
              {reports.length > 0 && (
                <Card className="border-0 shadow-lg mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-orange-500" />
                      Laporan Proyek
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isReportsLoading ? (
                      <div className="space-y-3">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="animate-pulse flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-2 h-2 bg-gray-200 rounded-full shrink-0"></div>
                            <div className="flex-1">
                              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                            </div>
                            <div className="w-16 h-8 bg-gray-200 rounded"></div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {reports.slice(0, 3).map((report: ProjectReport) => (
                          <div key={report.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0"></div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{report.title}</p>
                              <p className="text-sm text-gray-500">{report.summary}</p>
                              {report.reportDate && (
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(report.reportDate).toLocaleDateString('id-ID')}
                                </p>
                              )}
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigate(`/reports/${report.slug || report.id}`)}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Lihat
                            </Button>
                          </div>
                        ))}
                        
                        {/* Show "View All Reports" button if there are more than 3 reports */}
                        {reports.length > 3 && (
                          <div className="pt-3 border-t border-gray-200">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full"
                              onClick={() => navigate(`/reports?projectId=${id}`)}
                            >
                              Lihat Semua Laporan ({reports.length})
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-center">
            <Button
              onClick={() => navigate('/projects')}
              variant="outline"
              className="border-gray-300 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Kembali ke Semua Proyek
            </Button>
          </div>
        </div>
      </div>
  )
}